import moment from 'moment'
import { ISO_DATE_FORMAT } from '../../constants'
import { toContractMoment } from '../../date_format'
import { requests } from '../../requests/DefaultRequest'
import { CONTRACT_STATUS } from './mockData'

const CONTRACT_API_PREFIX = '/api/v1/contracts'

const STATUS_API_TO_UI = {
  draft: CONTRACT_STATUS.DRAFT,
  signed: CONTRACT_STATUS.SIGNED,
  active: CONTRACT_STATUS.ACTIVE,
  expiring: CONTRACT_STATUS.EXPIRING,
  expired: CONTRACT_STATUS.EXPIRED,
}

const STATUS_UI_TO_API = {
  [CONTRACT_STATUS.DRAFT]: 'DRAFT',
  [CONTRACT_STATUS.SIGNED]: 'SIGNED',
  [CONTRACT_STATUS.ACTIVE]: 'ACTIVE',
  [CONTRACT_STATUS.EXPIRING]: 'EXPIRING',
  [CONTRACT_STATUS.EXPIRED]: 'EXPIRED',
}

const normalizeStatusText = status => String(status || '').trim().toLowerCase()

const toNumber = value => {
  const normalizedValue = Number(value)
  return Number.isFinite(normalizedValue) ? normalizedValue : 0
}

export const mapApiStatusToUiStatus = status => {
  const normalizedStatus = normalizeStatusText(status)
  return STATUS_API_TO_UI[normalizedStatus] || normalizedStatus
}

export const mapUiStatusToApiStatus = status => {
  const normalizedStatus = normalizeStatusText(status)
  return STATUS_UI_TO_API[normalizedStatus] || String(status || '').trim().toUpperCase()
}

export const mapApiDetailToUi = detail => ({
  id: detail?.id ? String(detail.id) : undefined,
  serviceType: detail?.service_type || '',
  value: toNumber(detail?.price),
  startDate: detail?.start_date || '',
  endDate: detail?.end_date || '',
  note: detail?.remark || '',
})

export const mapApiAttachmentToUi = attachment => ({
  id: attachment?.file_id || '',
  fileId: attachment?.file_id || '',
  name: attachment?.file_name || attachment?.file_id || 'File',
  fileName: attachment?.file_name || attachment?.file_id || 'File',
  size: toNumber(attachment?.file_size || attachment?.size),
  uploadedAt:
    attachment?.uploaded_at ||
    attachment?.created_at ||
    attachment?.updated_at ||
    '',
})

export const mapApiContractToUi = contract => ({
  id: contract?.id ? String(contract.id) : '',
  contractNo: contract?.contract_code || '',
  supplierName: contract?.supplier || '',
  customerName: contract?.customer || '',
  signedDate: contract?.date_of_sign || '',
  effectiveDate: contract?.effective_date || '',
  status: mapApiStatusToUiStatus(contract?.status),
  description: contract?.description || '',
  totalValue: toNumber(contract?.total_price),
  totalService: toNumber(contract?.total_service),
  totalFile: toNumber(contract?.total_file),
  services: Array.isArray(contract?.details)
    ? contract.details.map(mapApiDetailToUi)
    : [],
  documents: Array.isArray(contract?.attachment)
    ? contract.attachment.map(mapApiAttachmentToUi)
    : [],
  createdAt: contract?.created_at,
  updatedAt: contract?.updated_at,
})

export const mapServiceFormToApi = service => ({
  service_type: service?.serviceType || '',
  price: toNumber(service?.value),
  start_date: service?.effectiveRange?.[0]?.format(ISO_DATE_FORMAT),
  end_date: service?.effectiveRange?.[1]?.format(ISO_DATE_FORMAT),
  remark: service?.note?.trim() || '',
})

export const mapFormValuesToContractPayload = ({
  values,
  status,
  attachmentIds,
}) => ({
  contract_code: values?.contractNo?.trim(),
  date_of_sign: values?.signedDate?.format(ISO_DATE_FORMAT),
  effective_date: values?.effectiveDate?.format(ISO_DATE_FORMAT),
  supplier: values?.supplierName?.trim(),
  customer: values?.customerName?.trim(),
  description: values?.description?.trim() || '',
  status: mapUiStatusToApiStatus(status),
  attachment: attachmentIds || [],
  details: (values?.services || []).map(mapServiceFormToApi),
})

export const parseContractToFormValues = contract => ({
  contractNo: contract.contractNo,
  status: contract.status || CONTRACT_STATUS.ACTIVE,
  signedDate: toContractMoment(contract.signedDate),
  effectiveDate: toContractMoment(contract.effectiveDate),
  supplierName: contract.supplierName,
  customerName: contract.customerName,
  description: contract.description,
  services: (contract.services || []).map(service => ({
    serviceId: service.id,
    serviceType: service.serviceType,
    value: service.value,
    effectiveRange: (() => {
      if (!service.startDate || !service.endDate) return []
      const startMoment = toContractMoment(service.startDate)
      const endMoment = toContractMoment(service.endDate)
      if (!startMoment || !endMoment) return []
      return [startMoment, endMoment]
    })(),
    note: service.note,
  })),
})

export const parseAttachmentsToUploadFiles = attachments => {
  return (attachments || []).map((attachment, index) => ({
    uid: attachment.fileId || attachment.id || `${Date.now()}-${index}`,
    name: attachment.fileName || attachment.name || 'File',
    size: toNumber(attachment.size),
    status: 'done',
    uploadedAt: attachment.uploadedAt || '',
    fileId: attachment.fileId || attachment.id || '',
  }))
}

export const generateContractCodeFromList = contracts => {
  const currentYear = moment().year()
  const sequenceValues = (contracts || [])
    .map(contract => String(contract?.contractNo || ''))
    .map(contractCode => {
      const matched = contractCode.match(/(\d{4})-(\d+)$/)
      if (!matched) return null
      const year = Number(matched[1])
      const sequence = Number(matched[2])
      if (year !== currentYear || Number.isNaN(sequence)) {
        return null
      }
      return sequence
    })
    .filter(sequence => Number.isInteger(sequence))

  const nextSequence = (Math.max(...sequenceValues, 0) || 0) + 1
  return `HD-${currentYear}-${String(nextSequence).padStart(5, '0')}`
}

export const ContractManagementRequest = {
  getContracts: params => requests.get(CONTRACT_API_PREFIX, true, params),
  getContractById: contractId =>
    requests.get(`${CONTRACT_API_PREFIX}/${contractId}`),
  createContract: payload => requests.post(CONTRACT_API_PREFIX, payload),
  updateContract: (contractId, payload) =>
    requests.put(`${CONTRACT_API_PREFIX}/${contractId}`, payload),
  deleteContract: contractId => requests.delete(`${CONTRACT_API_PREFIX}/${contractId}`),
  createContractDetail: (contractId, payload) =>
    requests.post(`${CONTRACT_API_PREFIX}/${contractId}/details`, payload),
  updateContractDetail: (contractId, detailId, payload) =>
    requests.put(`${CONTRACT_API_PREFIX}/${contractId}/details/${detailId}`, payload),
  deleteContractDetail: (contractId, detailId) =>
    requests.delete(`${CONTRACT_API_PREFIX}/${contractId}/details/${detailId}`),
}
